import { useUnsaved } from "@/lib/unsaved";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Wand2, Save, Info, Palette } from "lucide-react";
import { BrandLogo } from "@/components/BrandLogo";
import api from "@/lib/api";
import { useGuild } from "./DashboardLayout";
import { PageHead } from "@/components/kit";

const STATUSES = [
  { value: "online", label: "Online", color: "#22c55e" },
  { value: "idle", label: "Idle", color: "#f59e0b" },
  { value: "dnd", label: "Do Not Disturb", color: "#ef4444" },
  { value: "invisible", label: "Invisible", color: "#6b7280" },
];
const ACTIVITIES = [
  { value: "playing", label: "Playing" },
  { value: "listening", label: "Listening to" },
  { value: "watching", label: "Watching" },
  { value: "competing", label: "Competing in" },
  { value: "streaming", label: "Streaming" },
];

export default function BotPersonalizer() {
  const { guildId } = useGuild();
  const [cfg, setCfg] = useState(null);
  const [brand, setBrand] = useState(null);
  const [saving, setSaving] = useState(false);
  const save = useUnsaved({ cfg, brand }, (v) => { setCfg(v.cfg); setBrand(v.brand); }, () => saveRaw(), cfg != null && brand != null);

  useEffect(() => {
    api.get(`/guilds/${guildId}/personalizer`).then((r) => setCfg(r.data));
    api.get(`/guilds/${guildId}/branding`).then((r) => setBrand(r.data));
  }, [guildId]);
  const set = (k, v) => setCfg((c) => ({ ...c, [k]: v }));
  const setB = (k, v) => setBrand((b) => ({ ...b, [k]: v }));
  const saveRaw = async () => {
    setSaving(true);
    try {
      await api.put(`/guilds/${guildId}/personalizer`, cfg);
      if (brand) await api.put(`/guilds/${guildId}/branding`, brand);
      toast.success("Bot personalization saved — applies within ~2 min");
    }
    catch { toast.error("Failed to save"); }
    finally { setSaving(false); }
  };

  if (!cfg) return <p className="text-zinc-500">Loading…</p>;
  const statusMeta = STATUSES.find((s) => s.value === cfg.status) || STATUSES[0];
  const activityLabel = (ACTIVITIES.find((a) => a.value === cfg.activity_type) || ACTIVITIES[1]).label;

  return (
    <div>
      <PageHead title="Bot Personalizer" desc="Make your bot special — change its nickname, status and activity.">
        <button data-testid="personalizer-save" onClick={save} disabled={saving}
          className="bg-volt text-volt-ink font-semibold rounded-md px-5 py-2 hover:bg-volt-hover flex items-center gap-2 disabled:opacity-60">
          <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save"}
        </button>
      </PageHead>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-zinc-900 border border-zinc-800 rounded-xl p-6 space-y-5">
          <h3 className="font-head font-semibold flex items-center gap-2"><Wand2 className="w-4 h-4 text-volt" /> Basics</h3>

          <div>
            <label className="text-xs uppercase tracking-wider text-zinc-500">Bot nickname (this server)</label>
            <input data-testid="personalizer-nickname" value={cfg.nickname || ""} placeholder="Crank"
              onChange={(e) => set("nickname", e.target.value)} maxLength={32}
              className="mt-1 w-full bg-black border border-zinc-800 rounded-md px-3 py-2 outline-none focus:border-volt" />
            <p className="text-xs text-zinc-600 mt-1">The bot's nickname just in <span className="text-zinc-400">this server</span>. Leave blank to use its default name.</p>
          </div>

          <div className="flex items-start gap-2 bg-volt/5 border border-volt/20 rounded-lg p-3">
            <Info className="w-4 h-4 text-volt mt-0.5 shrink-0" />
            <p className="text-xs text-zinc-400">The bot's <strong>avatar/status/activity</strong> are set once and shared across every server (a Discord bot has a single global profile) — only the <strong>nickname</strong> can differ per server. Status &amp; activity below apply bot-wide.</p>
          </div>

          <div>
            <label className="text-xs uppercase tracking-wider text-zinc-500">Bot status</label>
            <select data-testid="personalizer-status" value={cfg.status} onChange={(e) => set("status", e.target.value)}
              className="mt-1 w-full bg-black border border-zinc-800 rounded-md px-3 py-2 outline-none focus:border-volt">
              {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs uppercase tracking-wider text-zinc-500">Activity type</label>
              <select data-testid="personalizer-activity-type" value={cfg.activity_type} onChange={(e) => set("activity_type", e.target.value)}
                className="mt-1 w-full bg-black border border-zinc-800 rounded-md px-3 py-2 outline-none focus:border-volt">
                {ACTIVITIES.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-zinc-500">Status text</label>
              <input data-testid="personalizer-activity-text" value={cfg.activity_text || ""} placeholder="/help"
                onChange={(e) => set("activity_text", e.target.value)} maxLength={128}
                className="mt-1 w-full bg-black border border-zinc-800 rounded-md px-3 py-2 outline-none focus:border-volt" />
            </div>
          </div>
        </div>

        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
          <h3 className="font-head font-semibold mb-4">Profile preview</h3>
          <div data-testid="personalizer-preview" className="bg-black rounded-lg overflow-hidden border border-zinc-800">
            <div className="h-16 bg-gradient-to-r from-volt/40 to-[#f59e0b]/30" />
            <div className="px-4 pb-4 -mt-6">
              <div className="w-16 h-16 rounded-full bg-zinc-800 border-4 border-black flex items-center justify-center relative overflow-hidden">
                <BrandLogo className="w-14 h-14" />
                <span className="absolute bottom-0 right-0 w-4 h-4 rounded-full border-2 border-black" style={{ background: statusMeta.color }} />
              </div>
              <div className="mt-2 flex items-center gap-2">
                <span className="font-head font-bold text-lg">{cfg.nickname || "Crank"}</span>
                <span className="text-[10px] font-bold bg-[#5865F2] text-[#fff] px-1 rounded">APP</span>
              </div>
              <div className="mt-3 bg-zinc-900 rounded-md p-3">
                <div className="text-[10px] uppercase tracking-wider text-zinc-500">{activityLabel}</div>
                <div className="text-sm text-zinc-200">{cfg.activity_text || "/help"}</div>
              </div>
            </div>
          </div>
          <p className="text-xs text-zinc-600 mt-3">Nickname is per-server; status &amp; activity apply to the bot everywhere.</p>
        </div>
      </div>

      {brand && (
        <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 bg-zinc-900 border border-zinc-800 rounded-xl p-6 space-y-5">
            <h3 className="font-head font-semibold flex items-center gap-2"><Palette className="w-4 h-4 text-volt" /> Embed branding</h3>
            <p className="text-xs text-zinc-500">This server's style — the bot reuses it on its embeds: level-ups, economy, leaderboards and more. Each server has its own.</p>
            <div className="flex items-center gap-3">
              <label className="text-xs uppercase tracking-wider text-zinc-500">Accent color</label>
              <input type="color" data-testid="brand-color" value={brand.accent_color || "#0EA5E9"} onChange={(e) => setB("accent_color", e.target.value)} className="h-9 w-16 bg-black border border-zinc-800 rounded-md cursor-pointer" />
              <span className="text-sm text-zinc-400 font-mono">{brand.accent_color}</span>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-zinc-500">Footer text</label>
              <input data-testid="brand-footer" value={brand.footer_text || ""} maxLength={128} onChange={(e) => setB("footer_text", e.target.value)}
                className="mt-1 w-full bg-black border border-zinc-800 rounded-md px-3 py-2 outline-none focus:border-volt" placeholder="Powered by Crank.live" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-zinc-500">Thumbnail / icon URL</label>
              <input data-testid="brand-thumbnail" value={brand.thumbnail_url || ""} onChange={(e) => setB("thumbnail_url", e.target.value)}
                className="mt-1 w-full bg-black border border-zinc-800 rounded-md px-3 py-2 outline-none focus:border-volt" placeholder="https://…/logo.png (optional)" />
            </div>
          </div>

          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
            <h3 className="font-head font-semibold mb-4">Embed preview</h3>
            <div data-testid="brand-preview" className="rounded-md overflow-hidden border-l-4 force-dark bg-[#111214]" style={{ borderColor: brand.accent_color || "#0EA5E9" }}>
              <div className="p-3 flex gap-3">
                <div className="flex-1">
                  <div className="font-semibold text-sm">Level up!</div>
                  <div className="text-sm text-zinc-300 mt-1">GG @Member, you reached level 5! 🎉</div>
                  {brand.footer_text && <div className="text-[11px] text-zinc-500 mt-3">{brand.footer_text}</div>}
                </div>
                {brand.thumbnail_url && <img src={brand.thumbnail_url} alt="" className="w-12 h-12 rounded object-cover bg-zinc-800 shrink-0" />}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
